import { Link, useLocation } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  Cpu,
  FileText,
  FolderTree,
  Home,
  KeyRound,
  Layers,
  type LucideIcon,
  PanelLeftClose,
  PlayCircle,
  Settings,
  Shield,
} from "lucide-react";

import { Logo } from "@/components/shared/logo";
import { cn } from "@/lib/utils";
import { ROUTES } from "@/lib/constants";
import { useSidebar } from "@/hooks/use-sidebar";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { isSuperAdmin } from "@/lib/roles";
import { Building2 } from "lucide-react";
import { UserMenu } from "./user-menu";
import { WorkspaceSelector } from "./workspace-selector";

interface NavItem {
  labelKey: TranslationKey;
  icon: LucideIcon;
  to: string;
}

interface NavGroup {
  labelKey: TranslationKey;
  items: ReadonlyArray<NavItem>;
}

// Sesi POLISH-NAV-API (v0.29.0) — sidebar nav grouped by workflow.
// Order reflects daily-use cadence for tenant admins: Dashboard
// first → AI operations (keys / providers / observability) →
// Governance (policy / asset / risk / compliance) → Pengaturan.
//
// "Penyedia AI" is promoted from a Settings tab to top-level here
// because procurement reviewers consistently flag BYOK setup as
// the first thing they look for after login.
//
// Labels resolve through the i18n layer at render time so the
// rail re-renders cleanly when the locale switches.
const NAV_GROUPS: ReadonlyArray<NavGroup> = [
  {
    labelKey: "navigation.groups.main",
    items: [
      { labelKey: "navigation.items.dashboard", icon: Home, to: ROUTES.DASHBOARD },
    ],
  },
  {
    labelKey: "navigation.groups.aiOperations",
    items: [
      { labelKey: "navigation.items.apiKeys", icon: KeyRound, to: ROUTES.API_KEYS },
      { labelKey: "navigation.items.aiProviders", icon: Cpu, to: ROUTES.PROVIDERS },
      {
        labelKey: "navigation.items.playground",
        icon: PlayCircle,
        to: ROUTES.PLAYGROUND,
      },
      {
        labelKey: "navigation.items.observability",
        icon: Activity,
        to: ROUTES.OBSERVABILITY,
      },
    ],
  },
  {
    labelKey: "navigation.groups.governance",
    items: [
      { labelKey: "navigation.items.policies", icon: FileText, to: ROUTES.POLICIES },
      { labelKey: "navigation.items.assets", icon: Shield, to: ROUTES.ASSETS },
      { labelKey: "navigation.items.skills", icon: Layers, to: ROUTES.SKILLS },
      { labelKey: "navigation.items.risk", icon: AlertTriangle, to: ROUTES.RISK },
      {
        labelKey: "navigation.items.evaluation",
        icon: CheckCircle2,
        to: ROUTES.EVALUATION,
      },
      {
        labelKey: "navigation.items.compliance",
        icon: BookOpen,
        to: ROUTES.COMPLIANCE,
      },
    ],
  },
  {
    labelKey: "navigation.groups.preferences",
    items: [
      {
        labelKey: "navigation.items.workspaces",
        icon: FolderTree,
        to: ROUTES.WORKSPACES,
      },
      { labelKey: "navigation.items.settings", icon: Settings, to: ROUTES.SETTINGS },
    ],
  },
];

/** Super-admin only group, rendered ONLY when the user.role is
 *  ``super_admin``. The route + BE 403 still apply if a tenant
 *  admin somehow lands on the URL; this just keeps the nav clean. */
const SUPER_ADMIN_NAV_GROUP: NavGroup = {
  labelKey: "navigation.groups.platform",
  items: [
    {
      labelKey: "navigation.items.platformTenants",
      icon: Building2,
      to: ROUTES.PLATFORM_TENANTS,
    },
  ],
};

export function Sidebar() {
  const location = useLocation();
  const { isCollapsed, toggleCollapsed } = useSidebar();
  const { t } = useTranslation();
  const { user } = useAuth();

  // Append the super-admin group only when the principal qualifies.
  // Tenant admins see the exact same nav they did pre-v0.42.0.
  const navGroups = isSuperAdmin(user?.role)
    ? [...NAV_GROUPS, SUPER_ADMIN_NAV_GROUP]
    : NAV_GROUPS;

  const labelClasses = cn(
    "overflow-hidden whitespace-nowrap transition-opacity duration-150",
    isCollapsed ? "w-0 opacity-0" : "opacity-100"
  );

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-20 hidden h-screen flex-col border-r border-ocean-100 bg-ocean-50 transition-[width] duration-200 ease-out tablet:flex",
        isCollapsed ? "w-sidebar-collapsed" : "w-sidebar-expanded"
      )}
    >
      {/* Header: logo on the left, collapse toggle on the right.
          When collapsed the entire header becomes a single click
          target that expands the rail — Anthropic Console style. */}
      <div className="h-14 border-b border-ocean-100">
        {isCollapsed ? (
          <button
            type="button"
            onClick={toggleCollapsed}
            title={t("navigation.sidebar.expand")}
            aria-label={t("navigation.sidebar.expand")}
            aria-expanded={false}
            className="flex h-full w-full items-center justify-center transition-colors hover:bg-ocean-100"
          >
            <Logo size="sm" showWordmark={false} />
          </button>
        ) : (
          <div className="flex h-full w-full items-center justify-between px-3">
            <Logo size="sm" />
            <button
              type="button"
              onClick={toggleCollapsed}
              title={t("navigation.sidebar.collapse")}
              aria-label={t("navigation.sidebar.collapse")}
              aria-expanded={true}
              className="rounded-md p-1.5 text-neutral-500 transition-colors hover:bg-ocean-100 hover:text-ocean-600"
            >
              <PanelLeftClose size={18} />
            </button>
          </div>
        )}
      </div>

      {/* Workspace selector */}
      <div className="border-b border-ocean-100 px-2 py-3">
        <WorkspaceSelector compact={isCollapsed} />
      </div>

      {/* Nav — workflow-grouped, ARIA navigation landmarks for AT. */}
      <nav
        aria-label={t("navigation.sidebar.ariaLabel")}
        className="flex-1 overflow-y-auto px-2 py-4"
      >
        <div className="space-y-5">
          {navGroups.map((group) => (
            <div key={group.labelKey}>
              {/* Group heading hidden in collapsed mode — labels
                  would clip awkwardly and the icons already group
                  visually. Still announced to screen readers via
                  the implicit nested ``<h3>``. */}
              {!isCollapsed && (
                <h3 className="px-2.5 pb-1 text-[10px] font-medium uppercase tracking-wide text-ocean-600">
                  {t(group.labelKey)}
                </h3>
              )}
              <ul className="space-y-1">
                {group.items.map((item) => (
                  <SidebarNavItem
                    key={item.to}
                    item={item}
                    label={t(item.labelKey)}
                    isCollapsed={isCollapsed}
                    labelClasses={labelClasses}
                    activePath={location.pathname}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      </nav>

      {/* User footer */}
      <div className="border-t border-ocean-100 px-2 py-3">
        <UserMenu compact={isCollapsed} />
      </div>
    </aside>
  );
}

function SidebarNavItem({
  item,
  label,
  isCollapsed,
  labelClasses,
  activePath,
}: {
  item: NavItem;
  label: string;
  isCollapsed: boolean;
  labelClasses: string;
  activePath: string;
}) {
  const Icon = item.icon;
  // Sub-route match: ``/settings/profile`` keeps the "Settings"
  // nav row active. Asset / policy / risk detail pages already
  // follow this pattern.
  const isActive =
    activePath === item.to || activePath.startsWith(`${item.to}/`);

  return (
    <li>
      <Link
        to={item.to}
        title={isCollapsed ? label : undefined}
        aria-current={isActive ? "page" : undefined}
        className={cn(
          "flex items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors",
          isActive
            ? "bg-ocean-100 font-medium text-ocean-900"
            : "text-neutral-600 hover:bg-ocean-100/50"
        )}
      >
        <Icon size={18} className="shrink-0" />
        <span className={labelClasses}>{label}</span>
      </Link>
    </li>
  );
}
